/**
 * Temu「车型库必填类目」（更新时间 2025-12-09）。
 *
 * 规则摘要：
 * - 全托：发布下列叶子类目时，若「适用车型维护=见详情」则兼容车型数据必填；=通用则非必填。
 * - 半托：仅当上架站点命中下列站点，且类目命中、适用车型=见详情时，兼容车型数据必填。
 */

import { TEMU_MODE_FULL, TEMU_MODE_SEMI, temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { attributeValueDisplayLabel } from '@/temu-platform/utils/temuAttributeTemplate';

const RAW_CATEGORY_IDS = `
20686,20692,20693,20699,20701,20702,20703,20706,20707,20711,20712,20719,20720,20736,20737,20738,20739,20740,20741,20746,20749,20750,20751,20753,20754,20774,20775,20776,20777,20778,20808,20824,20826,20834,20838,20843,20875,20876,20877,20878,20879,20881,20886,20887,20888,20889,20890,20891,20904,20908,20909,20917,20918,20919,21109,21110,21111,21112,21113,21117,21118,21119,21120,21121,21123,21124,21125,21126,21127,21128,21129,21130,21131,21132,21133,21134,21135,21136,21137,21138,21139,21140,21141,21142,21143,21144,21145,21146,21147,21149,21150,21151,21155,21156,21158,21159,21160,21161,21162,21163,21164,21165,21166,21168,21169,21170,21171,21536,21537,21538,21539,21540,21541,21543,21544,21545,21546,21547,21548,21549,21550,21551,21552,21553,21555,21556,21557,21558,21559,21560,21561,21562,21563,21565,21566,21567,21571,21572,21573,21575,21576,21577,21578,21580,21581,21582,21583,21585,21587,21588,21589,21590,21591,21592,21593,21595,21596,21597,21598,21599,21601,21603,21604,21606,21607,21608,21610,21611,21613,21614,21616,21617,21618,21619,21620,21621,21622,21623,21624,21625,21626,21627,21628,21629,21630,21632,21633,21634,21635,21636,21637,21638,21639,21640,21641,21642,21643,21644,21645,21646,21648,21649,21650,21651,21652,21653,21654,21655,21656,21658,21659,21660,21661,21662,21663,21674,21677,21681,21684,21685,21686,21687,20582,20645,20922,20923,20932,20936,20945,20947,21738,21767,21775,21776,21778,21779,21780,21781,21790,21799,21805,21873,21878,21879,21881,21892,21896,21897,21898,21899,21900,21901,21902,21904,21905,21906,21907,21908,21249,21250,21251,21252,21253,21254,21255,21256,21257,21258,21259,21260,21261,21262,21263,21264,21265,21266,21267,21268,21270,21271,21272,21273,21274,21275,21276,21277,21278,21279,21280,21281,21282,21283,21284,21285,21286,21287,21288,21291,21292,21293,21294,21295,21296,21297,21784,21786,21788,21792,20544,20545,20546,20547,20548,20565,20566,20567,20673,20674,20675,20676,20677,20679,20681,20682,21050,21051,21052,21053,21055,21056,21057,21058,21059,21060,21061,21062,21063,21064,21065,21066,21069,21070,21071,21072,21073,21074,21075,21076,21077,21079,21080,21081,21082,21083,21085,21086,21087,21088,21089,21090,21091,21092,21093,21094,21095,21097,21098,21099,21101,21103,21104,21105,21106,21107,21229,21230,21231,21232,21233,21234,21235,21236,21237,21238,21239,21240,21241,21242,21243,21420,21422,21434,21447,21452,21453,21457,21462,21463,21464,21469,21470,21471,21475,21476,21483,21487,21488,21489,21490,21491,21502,20446,20448,20450,20451,20452,20453,20454,20455,20456,20457,20459,20460,20461,20463,20464,20466,20468,20469,20470,20471,20472,20473,20474,20475,20476,20477,20479,20481,20482,20483,20484,20485,22176,22152,22184,22392,22049,22050,22034,22035,22036,22037,22038,22039,22040,22041,22042,22043,22044,22045,22046,22047,22048,22049,22050,22051,22052,22053,22054,22055,22056,22057,22058,22149,20724,20719
`;

/** 半托需命中的站点 siteId（公告清单） */
const RAW_SITE_IDS = `100,101,102,105,103,107,106,109`;

function freezeIdSet(raw) {
  return Object.freeze(new Set(
    String(raw || '')
      .split(/[,\s]+/)
      .map((s) => String(s || '').trim())
      .filter(Boolean),
  ));
}

export const TEMU_VEHICLE_LIBRARY_REQUIRED_CATEGORY_IDS = freezeIdSet(RAW_CATEGORY_IDS);
export const TEMU_VEHICLE_LIBRARY_REQUIRED_SITE_IDS = freezeIdSet(RAW_SITE_IDS);

export const TEMU_VEHICLE_FITMENT_SEE_DETAILS = 'see_details';
export const TEMU_VEHICLE_FITMENT_UNIVERSAL = 'universal';
export const TEMU_VEHICLE_FITMENT_UNKNOWN = 'unknown';
export const TEMU_VEHICLE_FITMENT_ABSENT = 'absent';

/**
 * @param {string|number|null|undefined} categoryId
 */
export function isTemuVehicleLibraryCategory(categoryId) {
  const id = String(categoryId ?? '').trim();
  if (!id) return false;
  return TEMU_VEHICLE_LIBRARY_REQUIRED_CATEGORY_IDS.has(id);
}

/**
 * @param {string|number|null|undefined} siteId
 */
export function isTemuVehicleLibraryRequiredSite(siteId) {
  const id = String(siteId ?? '').trim();
  if (!id) return false;
  return TEMU_VEHICLE_LIBRARY_REQUIRED_SITE_IDS.has(id);
}

/**
 * 半托已选站点是否与公告站点有交集（全托忽略站点）。
 * @param {string} shopType
 * @param {Array<string|number>} [selectedSites]
 */
export function isTemuVehicleLibrarySiteScopeActive(shopType, selectedSites = []) {
  const caps = temuShopCapabilities(shopType);
  if (caps.mode === TEMU_MODE_FULL) return true;
  if (caps.mode !== TEMU_MODE_SEMI) return false;
  const sites = Array.isArray(selectedSites) ? selectedSites : [];
  return sites.some((site) => isTemuVehicleLibraryRequiredSite(site));
}

/**
 * 类目 + 店铺类型/站点是否进入「车型库规则适用范围」（尚未看适用车型维护取值）。
 */
export function isTemuVehicleLibraryScopeActive({
  categoryId,
  shopType = '',
  selectedSites = [],
} = {}) {
  if (!isTemuVehicleLibraryCategory(categoryId)) return false;
  return isTemuVehicleLibrarySiteScopeActive(shopType, selectedSites);
}

function normalizeText(value) {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');
}

function classifyFitmentLabel(label) {
  const text = normalizeText(label);
  if (!text) return TEMU_VEHICLE_FITMENT_UNKNOWN;
  if (
    /见详情|详见|详情页|商品详情/.test(text)
    || /see\s*details?|see\s*product\s*details?|details?\s*page/.test(text)
  ) {
    return TEMU_VEHICLE_FITMENT_SEE_DETAILS;
  }
  if (/通用|universal|general|all\s*vehicles?|fits?\s*all/.test(text)) {
    return TEMU_VEHICLE_FITMENT_UNIVERSAL;
  }
  return TEMU_VEHICLE_FITMENT_UNKNOWN;
}

function isVehicleFitmentAttrName(name) {
  const text = normalizeText(name);
  if (!text) return false;
  return /适用车型|车型维护|vehicle\s*fitment|applicable\s*vehicle|vehicle\s*compatibility/.test(text);
}

function listProductAttrs(attrTpl) {
  if (!attrTpl || typeof attrTpl !== 'object') return [];
  if (Array.isArray(attrTpl.productAttrs) && attrTpl.productAttrs.length) return attrTpl.productAttrs;
  return [
    ...(attrTpl.requiredProduct || []),
    ...(attrTpl.optionalProduct || []),
    ...(attrTpl.mainAttrs || []),
    ...(attrTpl.otherAttrs || []),
  ];
}

function resolveAttrValueLabel(attr, rawVal) {
  if (rawVal == null || rawVal === '') return '';
  if (Array.isArray(rawVal)) {
    return rawVal
      .map((item) => {
        if (item && typeof item === 'object') {
          const byId = (attr?.values || []).find((v) => String(v?.id) === String(item.valueId || item.id || ''));
          return attributeValueDisplayLabel(byId) || attributeValueDisplayLabel(item) || String(item.valueId || item.id || item.name || '');
        }
        const byId = (attr?.values || []).find((v) => String(v?.id) === String(item));
        return attributeValueDisplayLabel(byId) || String(item);
      })
      .filter(Boolean)
      .join(' ');
  }
  const byId = (attr?.values || []).find((v) => String(v?.id) === String(rawVal));
  if (byId) return attributeValueDisplayLabel(byId) || String(rawVal);
  if (rawVal && typeof rawVal === 'object') {
    return attributeValueDisplayLabel(rawVal) || String(rawVal.name || rawVal.value || '');
  }
  return String(rawVal);
}

/**
 * 从类目属性中解析「适用车型维护」取值。
 * @returns {'see_details'|'universal'|'unknown'|'absent'}
 */
export function resolveTemuVehicleFitmentMode({ attrTpl = null, productAttributes = {} } = {}) {
  const attrs = listProductAttrs(attrTpl).filter((attr) => (
    isVehicleFitmentAttrName(attr?.name)
    || isVehicleFitmentAttrName(attr?.temu_prop?.name)
  ));
  if (!attrs.length) return TEMU_VEHICLE_FITMENT_ABSENT;

  const attrsMap = productAttributes && typeof productAttributes === 'object' ? productAttributes : {};
  for (const attr of attrs) {
    const key = String(attr.attributeId || attr.temu_prop?.templatePid || '').trim();
    if (!key) continue;
    const label = resolveAttrValueLabel(attr, attrsMap[key]);
    const mode = classifyFitmentLabel(label);
    if (mode === TEMU_VEHICLE_FITMENT_SEE_DETAILS || mode === TEMU_VEHICLE_FITMENT_UNIVERSAL) {
      return mode;
    }
  }
  return TEMU_VEHICLE_FITMENT_UNKNOWN;
}

/**
 * 兼容车型数据是否已填（relationId 或末级 dependency / ktype 任一非空）。
 */
export function hasTemuVehicleLibraryData(form = {}) {
  const rel = form?.goodsVehiclePropertyRelation;
  if (rel && typeof rel === 'object') {
    if (Number(rel.relationId) > 0) return true;
    const deps = Array.isArray(rel.leafPropertyValueDependencyIdList)
      ? rel.leafPropertyValueDependencyIdList
      : [];
    if (deps.some((id) => Number(id) > 0)) return true;
    const ktypes = Array.isArray(rel.ktype) ? rel.ktype : [];
    if (ktypes.some((id) => Number(id) > 0)) return true;
  }
  const fitments = Array.isArray(form?.vehicleFitments) ? form.vehicleFitments : [];
  return fitments.some((row) => {
    if (row == null) return false;
    if (typeof row === 'object') {
      return Number(row.id || row.leafPropertyValueDependencyId || row.dependencyId) > 0
        || Number(row.ktype) > 0;
    }
    return Number(row) > 0 || String(row).trim() !== '';
  });
}

/**
 * 公告完整必填判定：范围内 + 适用车型=见详情。
 */
export function isTemuVehicleLibraryDataRequired({
  categoryId,
  shopType = '',
  selectedSites = [],
  attrTpl = null,
  productAttributes = {},
} = {}) {
  if (!isTemuVehicleLibraryScopeActive({ categoryId, shopType, selectedSites })) {
    return false;
  }
  const mode = resolveTemuVehicleFitmentMode({ attrTpl, productAttributes });
  // 见详情 → 必填；通用 → 非必填；属性缺失/未知时：范围内仍提示必填（避免漏填被平台拒）
  if (mode === TEMU_VEHICLE_FITMENT_UNIVERSAL) return false;
  if (mode === TEMU_VEHICLE_FITMENT_SEE_DETAILS) return true;
  // 属性尚未选：不强制（等用户选完适用车型后再判）；仅见详情才拦截
  return false;
}

/**
 * 组装 goodsVehiclePropertyRelation（有数据才返回）。
 */
export function buildTemuGoodsVehiclePropertyRelation(form = {}) {
  const rel = form?.goodsVehiclePropertyRelation && typeof form.goodsVehiclePropertyRelation === 'object'
    ? form.goodsVehiclePropertyRelation
    : null;
  const fromFitments = (Array.isArray(form?.vehicleFitments) ? form.vehicleFitments : [])
    .map((row) => {
      if (row && typeof row === 'object') {
        return Number(row.id || row.leafPropertyValueDependencyId || row.dependencyId || 0);
      }
      return Number(row || 0);
    })
    .filter((id) => id > 0);
  const deps = Array.isArray(rel?.leafPropertyValueDependencyIdList)
    ? rel.leafPropertyValueDependencyIdList.map((id) => Number(id)).filter((id) => id > 0)
    : fromFitments;
  const ktypes = Array.isArray(rel?.ktype)
    ? rel.ktype.map((id) => Number(id)).filter((id) => id > 0)
    : [];
  const relationId = Number(rel?.relationId || 0);
  if (!(relationId > 0) && !deps.length && !ktypes.length) return null;
  const out = {
    relationType: Number(rel?.relationType || 1) || 1,
  };
  if (relationId > 0) out.relationId = relationId;
  if (deps.length) out.leafPropertyValueDependencyIdList = deps;
  if (ktypes.length) out.ktype = ktypes;
  return out;
}
